<template>
  <div :class="[bemBlock, 'image-container']">
    <a
      draggable="false"
      :class="bemElement('link')"
      :href="url"
      @click="handleLinkClick"
    >
      <BaseCarousel
        v-if="shouldShowCarousel"
        bem="base-product-tile-carousel"
        :are-controls-shown="areCarouselControlsShown"
        :items="productImageSets"
        :context-product-id="productId"
        :swiper-options="{ allowTouchMove: !isSliderContext }"
      >
        <template #default="slotProps">
          <BaseImage
            :class="slotProps.classname"
            :image-classes="bemElement('image')"
            :src="getDefaultImageUrlFromProductImageSet(slotProps.item)"
            :alt="getDefaultImageAltFromProductImageSet(slotProps.item)"
            :loading="
              forceMainImageToLoad && slotProps.index === 0
                ? 'eager'
                : slotProps.loading
            "
            :placeholder="
              getPlaceholderImageUrlFromProductImageSet(slotProps.item)
            "
            :sizes="sizes"
            :use-title="false"
            :srcset="getSrcsetFromProductImageSet(slotProps.item)"
            @load="handleImageLoad(slotProps.index)"
          />
        </template>
      </BaseCarousel>
      <BaseImage
        v-else
        :class="bemElement('pic')"
        :image-classes="bemElement('image')"
        :src="(mainImage || {}).url"
        :alt="(mainImage || {}).alt"
        :loading="forceMainImageToLoad ? 'eager' : null"
        :placeholder="mainPlaceholder"
        :sizes="sizes"
        :srcset="mainImageSrcset"
        :use-title="false"
        @load="handleImageLoad(0)"
      />
    </a>
  </div>
</template>

<script>
import bemClassesMixin from '../mixins/bemClassesMixin';
import productPropBaseMixin from '../mixins/productPropBaseMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import BaseCarousel from './BaseCarousel.vue';
import BaseImage from './BaseImage.vue';
import getFrontConfig from '../../common/getFrontConfig';
import sitePreferencesConfig from '../../../../../config/sitePreferencesConfig.json';
import { applyImageTransformationsToUrl } from '../../common/utils/imageTransformation';
import windowResizeMixin from '../mixins/windowResizeMixin';

/*
img sources
min-width:768px => 25w
50w

srcset:
200px,
400px,
600px,
800px,
1200px,

Resolutions - Tile img size
375 x ...: 186x278 (1x), 372x556 (2x)
768 x ...: 190x285 (1x), 380x570 (2x)
1024 x ...: 254x381 (1x), 508x762 (2x)
1440 x 900: 328x493 (1x), 656x986 (2x)
2560 x 1080: 586x879
2560 x 1600 (Macbook retina 13"): 1172x1758 (2x)
5120 x 2880: 1174x1761 (1x), 2348x3522 (2x)
*/
const SIZES = {
  MINICART_SLIDER:
    '(min-width: 1400px) 7vw, (min-width: 1025px) 10vw, (min-width: 768px) 19vw, 30vw',
  PDP_COMPLETE_THE_LOOK:
    '(min-width: 1025px) 16.6vw, (min-width: 768px) 28vw, 40vw',
  PLP_SLIDER: '40vw',
  PLP_GRID: '(min-width: 768px) 25vw, 50vw',
  PLP_GRID_FULL_WIDTH: '(min-width: 768px) 25vw, 100vw',
  SEARCH_MODAL:
    '(min-width: 1400px) 7vw, (min-width: 1025px) 10vw, (min-width: 768px) 19vw, 30vw',
};

// Widths used to build the srcset from a single base image URL (matches the
// PLAIN image set previously sent by the server).
const SLIDER_SRCSET_WIDTHS = [300, 600];
// Small width used to derive a blurry placeholder from the single base URL.
const PLACEHOLDER_WIDTH = 44;

const {
  productImages: { imageSets, placeholderType },
  responsive: { bp_768: bp768 },
  vue: {
    BaseProductTile: { context },
  },
} = getFrontConfig();

const BLURRY_PLACEHOLDER =
  sitePreferencesConfig.customAttributes.imagePlaceholderType.values
    .blurryImage;

export default {
  name: 'BaseProductTileImage',

  components: {
    BaseCarousel,
    BaseImage,
  },

  mixins: [
    bemClassesMixin,
    productPropBaseMixin,
    productImageSetMixin,
    windowResizeMixin,
  ],

  props: {
    hasCarousel: {
      type: Boolean,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    context: {
      type: String,
      required: false,
      default: context.plpGrid,
    },
    forceMainImageToLoad: {
      default: false,
      required: false,
      type: Boolean,
    },
    areCarouselControlsShown: {
      default: true,
      type: Boolean,
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp768,
  }),

  computed: {
    shouldShowCarousel() {
      // A single base image is authoritative: it never renders as a carousel.
      if (this.singleImage) return false;
      return this.hasCarousel && this.productImageSets?.length > 1;
    },
    isSliderContext() {
      return (
        this.context === context.minicartSlider ||
        this.context === context.minicartSliderEmptyCart ||
        this.context === context.pdpSlider ||
        this.context === context.plpSliderBanner ||
        this.context === context.pdpRecommendedSlider ||
        this.context === context.productSet ||
        this.context === context.homeRecommendedEinsteinProducts ||
        this.context === context.searchModal
      );
    },
    /**
     * The single Image ({alt, url}) passed directly on the product. When
     * present, the default image, placeholder and srcset are derived from it
     * client-side; otherwise the product is rendered from its full image set.
     * @returns {Image|null} the single image, or null when rendered from a set
     */
    singleImage() {
      return this.product.image?.url ? this.product.image : null;
    },
    /**
     * First image set, used when the product is not rendered from a single image.
     * @returns {ProductImageSet} the first image set
     */
    mainImageSet() {
      return this.productImageSets?.[0];
    },
    /**
     * Image shown by the single (non-carousel) BaseImage.
     * @returns {Image|Object} the default image to render
     */
    mainImage() {
      return (
        this.singleImage ??
        this.getDefaultImageFromProductImageSet(this.mainImageSet)
      );
    },
    /**
     * Placeholder URL for the single image: derived from the base URL (scaled
     * down) in single-image mode, taken from the image set otherwise. Null when
     * blurry placeholders are disabled.
     * @returns {string|null} placeholder URL, or null when disabled
     */
    mainPlaceholder() {
      if (this.singleImage) {
        if (placeholderType !== BLURRY_PLACEHOLDER) return null;
        return applyImageTransformationsToUrl(this.singleImage.url, {
          sw: PLACEHOLDER_WIDTH,
        });
      }
      return (
        this.getPlaceholderImageFromProductImageSet(this.mainImageSet)?.url ??
        null
      );
    },
    /**
     * srcset for the single (non-carousel) image. In single-image mode the
     * srcset is computed client-side from the base URL; otherwise it falls back
     * to the image-set srcset.
     * @returns {string} srcset value for the main image
     */
    mainImageSrcset() {
      if (this.singleImage) {
        return this.getSrcsetFromUrl(
          this.singleImage.url,
          SLIDER_SRCSET_WIDTHS
        );
      }
      return this.getSrcsetFromProductImageSet(this.mainImageSet);
    },
    productImageSets() {
      let imageSet = imageSets.PLP;
      let fallbackImageSet = null;
      if (
        this.context === context.plpGridFullWidth &&
        this.isBreakpointMobile
      ) {
        imageSet = imageSets.PLP_FULL_WIDTH;
        fallbackImageSet = imageSets.PLP;
      } else if (
        this.isSliderContext &&
        this.context !== context.plpSliderBanner
      ) {
        // The grid banner slider renders single base images on mobile/skeleton;
        // on desktop it loads the full tile and uses the product's own PLP set
        // (the default), like a normal grid tile (SF-11727/SF-11428).
        imageSet = imageSets.PLAIN;
      }
      let result = this.getProductImageSets(imageSet);
      if (result.length === 0 && fallbackImageSet) {
        result = this.getProductImageSets(fallbackImageSet);
      }
      return result;
    },
    sizes() {
      let result = SIZES.PLP_GRID;
      if (this.context === context.plpGridFullWidth) {
        result = SIZES.PLP_GRID_FULL_WIDTH;
      } else if (this.context === context.pdpSlider) {
        result = SIZES.PDP_COMPLETE_THE_LOOK;
      } else if (this.context === context.minicartSlider) {
        result = SIZES.MINICART_SLIDER;
      } else if (this.context === context.plpSliderBanner) {
        result = SIZES.PLP_SLIDER;
      } else if (this.context === context.searchModal) {
        result = SIZES.SEARCH_MODAL;
      }
      return result;
    },
  },

  methods: {
    handleImageLoad(index) {
      if (+index === 0) {
        this.$emit('load-main-image');
      }
    },
    handleLinkClick($event) {
      if (!$event.ctrlKey) {
        $event.preventDefault();
        const isCarouselControlClick = !!$event.target.closest(
          '.base-product-tile-carousel__control'
        );
        if (isCarouselControlClick) {
          // if the click is on a control/arrow of the carousel, abort the click
          return;
        }
        this.$emit('click', $event);
      }
    },
  },
};
</script>
